import { ItemCategory, ItemCategoryResponse, ApiResponse } from "../models/ItemCategoryTypes";

const getAuthToken = () => localStorage.getItem("authToken") || "";
const API_URL = process.env.REACT_APP_API_URL || "https://api-agamhive.agamvanigam.com";
export const UNAUTHORIZED_EVENT = "unauthorized_access";
interface FetchParams {
  page?: number;
  limit?: number;
  search?: string;
  filter?: Record<string, string | number | boolean>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const handleUnauthorized = () => {
    console.error("Unauthorized access. Dispatching event for navigation...");
    localStorage.removeItem("authToken");
    
    const unauthorizedEvent = new CustomEvent(UNAUTHORIZED_EVENT);
    window.dispatchEvent(unauthorizedEvent);
    return { error: "Unauthorized. Please log in." };
};

export const fetchItemCategoryData = async (params: FetchParams): Promise<ApiResponse<any>> => {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();

  try {
    const queryParams = new URLSearchParams({
      page: (params.page || 1).toString(),
      limit: (params.limit || 10).toString(),
      ...(params.search ? { search: params.search } : {}),
      ...(params.filter
        ? Object.fromEntries(Object.entries(params.filter).map(([key, value]) => [key, String(value)]))
        : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    }); 
    
    const response = await fetch(`${API_URL}/itemcategorys?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (response.status === 401) {
      return handleUnauthorized();
  }


    const jsonResponse = await response.json();
    if (!response.ok) {
      return { error: jsonResponse.msg || "Failed to fetch item categories" };
    }

    return {
      data: {
        data: jsonResponse.data.data || [], 
        pagination: {
          total: jsonResponse.data.pagination.total || 0,
          page: jsonResponse.data.pagination.page || 1,
          limit: jsonResponse.data.pagination.limit || 10,
          totalPages: jsonResponse.data.pagination.totalPages || 1,
        },
      },
    };
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
};

export async function createItemCategory(itemcategory: Omit<ItemCategory, 'status'>): Promise<ApiResponse<ItemCategory>> {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();

  try {
    const response = await fetch(`${API_URL}/itemcategorys`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(itemcategory),
    });
    if (response.status === 401) {
      return handleUnauthorized();
  }
    const data = await response.json();
    return response.ok ? { data } : { error: data?.msg || "Failed to create itemcategory." };
  } catch (error) {
    return { error: "Network error. Please check your connection and try again." };
  }
}


export async function updateItemCategory(id: string, itemcategory: ItemCategory): Promise<ApiResponse<ItemCategory>> {
    const token = getAuthToken();
    if (!token) return handleUnauthorized();
  
  
    const requestBody = {
      code: itemcategory.code,
      name: itemcategory.name,
      status: itemcategory.status,
      updatedAt: new Date().toISOString(), 
    };
  
    try {
      const response = await fetch(`${API_URL}/itemcategorys/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      if (response.status === 401) {
        return handleUnauthorized();
    }
      const data = await response.json();
  
  
      if (!response.ok) {
        console.error(" Update Failed:", data?.msg || "Unknown error");
        return { error: data?.msg || "Failed to update itemcategory." };
      }
  
      return { data };
    } catch (error) {
      console.error(" Network error:", error);
      return { error: "Network error. Please try again." };
    }
  }
  


export async function deleteItemCategory(id: string): Promise<ApiResponse<null>> {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();

  try {
    const response = await fetch(`${API_URL}/itemcategorys/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 401) {
      return handleUnauthorized();
  }

    return response.ok ? { data: null } : { error: "Failed to delete itemcategory." };
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
}
